// BootstrapApi — the aggregated `/accounts/{id}/bootstrap` call (contract 07 §6, canonical
// api/ contract 03 §15). Imports ONLY an http instance + own models and returns the unwrapped
// DTO, never an AxiosResponse. `suppressGlobalErrorHandler: true` stays — the boot flow owns
// the bootstrap error taxonomy.
//
// R5 / BE-07-01: `/accounts/{id}/bootstrap` returns the raw `{meta, errors, <sections>}` body
// (no `{status,data}` envelope), so this uses the flat `unwrap`, not `unwrapEnvelope`.
import type { AxiosInstance } from 'axios'

import type { BootstrapResponse, BootstrapSection } from '../models/bootstrap.types'

import { appHttp, unwrap } from '@/shared/http'

export interface BootstrapParams {
  /** `0` → first-login server-resolve (BE-07-08). */
  accountId: number
  sections?: BootstrapSection[] | undefined
  workspaceId?: number | string | undefined
  refresh?: boolean | undefined
  /** `false` keeps dimensions metadata but omits its dropdown values (BE v2.4.0). */
  dropdownValues?: boolean | undefined
  scheduleId?: string | undefined
  cursor?: string | undefined
}

export class BootstrapApi {
  // Injectable for tests; defaults to the singleton instance.
  constructor(private readonly http: AxiosInstance = appHttp) {}

  async fetchBootstrap(params: BootstrapParams): Promise<BootstrapResponse> {
    const { accountId, sections, workspaceId, refresh, dropdownValues, scheduleId, cursor } = params

    const query: Record<string, string> = {}
    if (sections?.length) query.sections = sections.join(',')
    if (workspaceId !== undefined && workspaceId !== null) query.workspace_id = String(workspaceId)
    if (refresh) query.refresh = 'true'
    // `true` is the backend default; omit it to avoid a redundant bootstrap cache-key variant.
    if (dropdownValues === false) query.dropdown_values = 'false'
    if (scheduleId) query.schedule_id = scheduleId
    if (cursor) query.cursor = cursor

    return unwrap(
      await this.http.get<BootstrapResponse>(`/accounts/${accountId}/bootstrap`, {
        params: query,
        suppressGlobalErrorHandler: true,
        // The boot flow classifies its own 401 and the shell wires it to logout (useBootstrap).
        meta: { ownsUnauthorized: true },
      }),
    )
  }
}

export const bootstrapApi = new BootstrapApi()
